import React, { FC, useEffect, useCallback, useState, KeyboardEvent } from 'react';
import { SearchSelect, SearchSelectItem } from '@td-client-frontend/common';
import { useTemplates } from '../../../hooks/useTemplates';
import { useDebounce } from 'use-debounce';

interface TemplateSearchSelectProps {
  name: string;
  label: string;
  placeholder: string;
  error?: string;
  efficientValueLength?: number;
  noSearchResultsMessage?: string;
  searchValue: string;
  setSearchValue: (value: string) => void;
}

export const TemplateSearchSelect: FC<TemplateSearchSelectProps> = ({
  name,
  label,
  error,
  placeholder,
  efficientValueLength,
  noSearchResultsMessage,
  searchValue,
  setSearchValue
}) => {
  const [options, setOptions] = useState<SearchSelectItem[]>([]);
  const [immediately, setImmediately] = useState<boolean>(false);

  const [debounceTemplateName] = useDebounce(searchValue, 750);

  const { data, isLoading } = useTemplates(immediately ? searchValue : debounceTemplateName);

  const onSelect = useCallback(
    (option: SearchSelectItem) => {
      setSearchValue(option.label);
    },
    [setSearchValue]
  );

  const onSearchChange = useCallback(
    (search: string) => {
      if (!search) setSearchValue('');
      setSearchValue(search);
    },
    [setSearchValue]
  );

  const onEnterKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && searchValue.length >= 3) {
        setImmediately(true);
      } else {
        setImmediately(false);
      }
    },
    [searchValue.length]
  );

  useEffect(() => {
    if (debounceTemplateName.length >= 3) {
      const newOptions = data?.map((templateName: string) => ({ value: templateName, label: templateName }));
      setOptions(newOptions);
    } else {
      setOptions([]);
    }
  }, [debounceTemplateName, data]);

  return (
    <SearchSelect
      name={name}
      placeholder={placeholder}
      options={options}
      value={searchValue}
      onSearchChange={onSearchChange}
      label={label}
      onSelect={onSelect}
      error={error}
      onKeyDown={onEnterKeyDown}
      dataIsLoading={isLoading}
      efficientValueLength={efficientValueLength}
      noSearchResultsMessage={noSearchResultsMessage}
      required
    />
  );
};
